import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { FormHelperText, SelectChangeEvent } from '@mui/material';
import { omit } from 'lodash';
import React from 'react';
import { InputStatus } from 'src/components/input/Input.interface';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { copyToClipboard, MenuProps } from 'src/utils';
import { lang } from 'src/utils/language';

import { OptionType, WidgetsCommonProps } from '../widget.interface';
import {
  DropdownFooterButton,
  HoverIconButton,
  SelectBox,
  SelectBoxMenuItem,
  SelectBoxMenuItemText,
  StyledInputLabel,
  StyledInputLabelText,
  Wrapper,
} from './SelectDropdownWidget.styled';

const MenuElement = ({
  width,
  label,
}: {
  width: number | null | undefined;
  label: string;
}) => {
  return (
    <CustomTooltip
      width={500}
      placement="right"
      title={label}
      {...(width
        ? {
            overflowWidth: width,
          }
        : {
            overflowLineNumber: 1,
          })}>
      <SelectBoxMenuItemText $width={width}>{label}</SelectBoxMenuItemText>
    </CustomTooltip>
  );
};

export const SelectDropdownWidget = React.forwardRef(
  (props: WidgetsCommonProps, ref) => {
    const { selectOptions = [] } = props;

    const handleChange = (event: SelectChangeEvent) => {
      props.onChange(event?.target?.value);
    };

    const handleCopyClick = (text: string) => async (e: React.MouseEvent) => {
      e.stopPropagation();

      copyToClipboard(text);
    };

    const menuProps = props.options?.showFooterButton
      ? {
          ...omit(MenuProps, 'PaperProps.style.maxHeight'),
          MenuListProps: {
            style: {
              paddingBottom: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            },
          },
        }
      : MenuProps;

    return (
      <Wrapper error={props.hasError}>
        <InputTextLabel
          {...(props.disabled ? { status: InputStatus.DISABLED } : {})}>
          {props.label}
        </InputTextLabel>
        <StyledInputLabel
          $skinny={props.skinny}
          $hasLabel={Boolean(props.label)}
          shrink={Boolean(props.value)}>
          <StyledInputLabelText $width={props.menuItemWidth}>
            {props?.options?.placeholder}
          </StyledInputLabelText>
        </StyledInputLabel>
        <SelectBox
          $skinny={props.skinny}
          disabled={props.disabled}
          onChange={handleChange}
          value={props.value ?? ''}
          inputRef={ref}
          error={props.hasError}
          MenuProps={menuProps}
          IconComponent={props.IconComponent}>
          {selectOptions.map((option: OptionType, index: number) => (
            <SelectBoxMenuItem
              value={option.value}
              key={index}
              disabled={option?.disabled}>
              <MenuElement width={props.menuItemWidth} label={option.label} />
              {props.canCopy && (
                <HoverIconButton onClick={handleCopyClick(option.label)}>
                  <ContentCopyIcon />
                </HoverIconButton>
              )}
            </SelectBoxMenuItem>
          ))}

          {props.options?.showFooterButton && (
            <DropdownFooterButton
              fullWidth
              variant="text"
              startIcon={props.footerButtonIcon ?? <AddIcon />}
              onMouseDown={(event: React.MouseEvent) => {
                event.preventDefault();
                props.onFooterButtonClick?.();
              }}>
              {props.footerButtonLabel ?? lang.addNewItem}
            </DropdownFooterButton>
          )}
        </SelectBox>
        <FormHelperText>
          {props.hasError ? props.error : undefined}
        </FormHelperText>
      </Wrapper>
    );
  },
);
